import type {ReactElement} from 'react';
import React, {useState} from 'react';
import {Text, TextSize} from '@Components/text';
import {DateInput} from '@Components/event-wizard/date-input';
import {TimeInput} from '@Components/time-input';
import {DateTimeErrorType, endTimeInputDropdownID, MAX_EVENT_LENGTH_DAYS, startTimeInputDropdownID} from '@Components/event-wizard/event-wizard-date-time/event-wizard-date-time.types';
import {EventWizardErrorInfo} from '@Components/event-wizard/event-wizard-error-info';
import {Timezone} from '@Components/event-wizard/timezone';
import {RecurringEventSelector} from '@Components/event-wizard/recurring-event-selector';
import {
  onEventWizardDateTimeMobilePanelUpdate,
  onEventWizardEndDateTimeUpdate,
  onEventWizardRecurringEndsOnChange,
  onEventWizardRecurringWeeklyChange,
  onEventWizardStartDateTimeUpdate,
  onEventWizardToggleCountdown,
} from '@Components/event-wizard/event-wizard-slice';
import {updatePreviewCountdown, updatePreviewDateTime} from '@Components/event-wizard/preview/preview.helper';
import {useDispatch} from 'react-redux';
import {
  useEventWizardCountdown,
  useEventWizardDateTimeMobilePanel,
  useEventWizardEndDateTime,
  useEventWizardRecurringData,
  useEventWizardRecurringLastDate,
  useEventWizardRecurringType,
  useEventWizardStartDateTime,
  useEventWizardTimeZone,
} from '@Components/event-wizard/event-wizard.hooks';
import {
  getDateTimeErrorType,
  getMaxAllowedDate,
  isEventLengthValid,
  makeDatesEqualForRecurringEventFromEndUpdate,
  makeDatesEqualForRecurringEventFromStartUpdate,
  shouldEnsureRecurringEventIsOnSameDay,
} from '@Components/event-wizard/event-wizard-date-time/event-wizard-date-time.helper';
import type {EventWizardDateTimeContentProps} from '@Components/event-wizard/event-wizard-date-time-content/event-wizard-date-time-content.types';
import {DateTimeMobilePanel} from '@Panels/event-wizard-date-time-panel/event-wizard-date-time-panel.types';
import {TimezoneContent} from '@Components/event-wizard/timezone-content';
import {EventWizardRecurringTypeContent} from '@Components/event-wizard/event-wizard-recurring-type-content';
import type {TimeZone} from 'timezones-list';
import {DateInputCalendarContent} from '@Components/event-wizard/date-input-calendar-content';
import {getMaxRecurringDate} from '@Components/event-wizard/recurring-event-selector/recurring-event-selector.helper';
import {CalendarTimePicker} from '@Components/calendar-time-picker';
import {convert12HrTo24Hr, convert24To12Hr, is24hrPM} from '@Components/time-input/time-input.helper';
import {DayNightSelector} from '@Components/time-input/time-input.types';
import type {CalendarItemBaseStorage} from '@Components/calendar-date-time-picker/calendar.types';
import {ALL_WEEKDAYS_USA} from '@Components/event-wizard/week-day-selectors/week-day-selectors.types';
import styles from './event-wizard-date-time-content.module.scss';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {ToggleSwitch, ToggleSwitchV2Size} from '@Components/toggle-switch-v2';

export function EventWizardDateTimeContent({isMobile = false}: EventWizardDateTimeContentProps): ReactElement<any> {
  const dispatch = useDispatch();

  const [errMsg, setErrMsg] = useState<string | undefined>();
  const [isTimeErr, setIsTimeErr] = useState<boolean>(false);
  const [isDateErr, setIsDateErr] = useState<boolean>(false);

  const selectedStartDateTime = useEventWizardStartDateTime();
  const selectedEndDateTime = useEventWizardEndDateTime();
  const isShowCountdown = useEventWizardCountdown();
  const recurringPattern = useEventWizardRecurringData();
  const recurringType = useEventWizardRecurringType();
  const timezone = useEventWizardTimeZone();
  const dateTimeMobilePanel = useEventWizardDateTimeMobilePanel();
  const lastRecurringDate = useEventWizardRecurringLastDate();

  const updateErrorState = (startDateTime: Date, endDateTime: Date, selectedTimezone: TimeZone): void => {
    const errType: null | DateTimeErrorType = getDateTimeErrorType(startDateTime, endDateTime, selectedTimezone);

    if (errType === null) {
      setErrMsg(undefined);
      setIsDateErr(false);
      setIsTimeErr(false);
      return;
    }

    if (errType === DateTimeErrorType.EVENT_PASSED_DATE) {
      setErrMsg(window.i18next.t('pmwjs_event_date_passed'));
      setIsDateErr(true);
      setIsTimeErr(false);
      return;
    }

    if (errType === DateTimeErrorType.EVENT_PASSED_TIME) {
      setErrMsg(window.i18next.t('pmwjs_event_time_passed'));
      setIsDateErr(false);
      setIsTimeErr(true);
      return;
    }

    if (errType === DateTimeErrorType.END_DATE_GREATER) {
      setErrMsg(window.i18next.t('pmwjs_end_date_greater_than_start'));
      setIsDateErr(true);
      setIsTimeErr(false);
      return;
    }

    if (errType === DateTimeErrorType.EVENT_TOO_LONG_DAYS) {
      setErrMsg(window.i18next.t('pmwjs_event_cannot_exceed_x_days', {days: MAX_EVENT_LENGTH_DAYS}));
      setIsDateErr(true);
      setIsTimeErr(false);
      return;
    }

    setErrMsg(window.i18next.t('pmwjs_end_time_greater_than_start'));
    setIsDateErr(false);
    setIsTimeErr(true);
  };

  const onStartTimeUpdateFromAmPmToggleOrDropdown = (hour: number, minute: number, isAmPmToggle: boolean, isFromDropdown: boolean): void => {
    if (isAmPmToggle || isFromDropdown) {
      updateSelectedStartTime(hour, minute);
    }
  };

  const onEndTimeUpdateFromAmPmToggleOrDropdown = (hour: number, minute: number, isAmPmToggle: boolean, isFromDropdown: boolean): void => {
    if (isAmPmToggle || isFromDropdown) {
      updateSelectedEndTime(hour, minute);
    }
  };

  const updateLastRecurringDate = (newDate: Date): void => {
    dispatch(onEventWizardRecurringEndsOnChange(newDate));
  };

  const updateSelectedStartTimeFromTimePicker = (time: CalendarItemBaseStorage): void => {
    const hr24 = convert12HrTo24Hr(time.isPMChecked ? DayNightSelector.PM : DayNightSelector.AM, time.hours);
    updateSelectedStartTime(hr24, time.minutes);
  };

  const updateSelectedEndTimeFromTimePicker = (time: CalendarItemBaseStorage): void => {
    const hr24 = convert12HrTo24Hr(time.isPMChecked ? DayNightSelector.PM : DayNightSelector.AM, time.hours);
    updateSelectedEndTime(hr24, time.minutes);
  };

  const updateSelectedStartTime = (hour: number, minute: number): void => {
    let newStartTime = new Date(selectedStartDateTime.getTime());
    newStartTime.setHours(hour, minute, 0, 0);

    const isStartTimeAheadOfEndTime = newStartTime >= selectedEndDateTime;
    let newEndTimeFromErrState = selectedEndDateTime;

    if (isStartTimeAheadOfEndTime) {
      newEndTimeFromErrState = new Date(newStartTime.getTime());
      newEndTimeFromErrState.setHours(newEndTimeFromErrState.getHours() + 1);
    }

    if (shouldEnsureRecurringEventIsOnSameDay(recurringPattern, newStartTime, newEndTimeFromErrState)) {
      [newStartTime, newEndTimeFromErrState] = makeDatesEqualForRecurringEventFromStartUpdate(newStartTime);
    }
    dispatch(onEventWizardStartDateTimeUpdate(newStartTime));

    if (isStartTimeAheadOfEndTime) {
      dispatch(onEventWizardEndDateTimeUpdate(newEndTimeFromErrState));
    }
    updateErrorState(newStartTime, newEndTimeFromErrState, timezone);

    updatePreviewDateTime({
      startDateTime: newStartTime,
      endDateTime: newEndTimeFromErrState,
      recurringPattern: recurringPattern,
      timeZone: timezone,
    });
    updatePreviewCountdown(isShowCountdown ? newStartTime : undefined, timezone);
  };

  const updateSelectedEndTime = (hour: number, minute: number): void => {
    let newEndTime = new Date(selectedEndDateTime.getTime());
    newEndTime.setHours(hour, minute, 0, 0);

    const isStartTimeAheadOfEndTime = newEndTime <= selectedStartDateTime;
    let newStartTimeFromErrState = selectedStartDateTime;

    if (isStartTimeAheadOfEndTime) {
      newStartTimeFromErrState = new Date(newEndTime.getTime());
      newStartTimeFromErrState.setHours(newStartTimeFromErrState.getHours() - 1);
    }

    if (shouldEnsureRecurringEventIsOnSameDay(recurringPattern, newStartTimeFromErrState, newEndTime)) {
      [newStartTimeFromErrState, newEndTime] = makeDatesEqualForRecurringEventFromEndUpdate(newEndTime);
    }
    dispatch(onEventWizardEndDateTimeUpdate(newEndTime));

    if (isStartTimeAheadOfEndTime) {
      dispatch(onEventWizardStartDateTimeUpdate(newStartTimeFromErrState));
    }
    updateErrorState(newStartTimeFromErrState, newEndTime, timezone);

    updatePreviewDateTime({
      startDateTime: newStartTimeFromErrState,
      endDateTime: newEndTime,
      recurringPattern: recurringPattern,
      timeZone: timezone,
    });
  };

  const updateSelectedStartDate = (date: Date): void => {
    const newStartDate = new Date(selectedStartDateTime.getTime());
    newStartDate.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
    dispatch(onEventWizardStartDateTimeUpdate(newStartDate));
    dispatch(onEventWizardRecurringWeeklyChange([ALL_WEEKDAYS_USA[newStartDate.getDay()]]));
    updateErrorState(newStartDate, selectedEndDateTime, timezone);

    updatePreviewDateTime({
      startDateTime: newStartDate,
      endDateTime: selectedEndDateTime,
      recurringPattern: recurringPattern,
      timeZone: timezone,
    });
    updatePreviewCountdown(isShowCountdown ? newStartDate : undefined, timezone);

    let updatedEndDateTime = selectedEndDateTime;

    if (newStartDate.getTime() > selectedEndDateTime.getTime() || shouldEnsureRecurringEventIsOnSameDay(recurringPattern, newStartDate, selectedEndDateTime)) {
      updatedEndDateTime = newStartDate;
      updateSelectedEndDate(newStartDate, newStartDate);
    }

    if (!isEventLengthValid(newStartDate, updatedEndDateTime)) {
      updateSelectedEndDate(getMaxAllowedDate(newStartDate), newStartDate);
    }
  };

  const updateSelectedEndDate = (endDate: Date, startDate: Date = selectedStartDateTime): void => {
    const newEndDate = new Date(selectedEndDateTime.getTime());
    newEndDate.setFullYear(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    dispatch(onEventWizardEndDateTimeUpdate(newEndDate));
    updateErrorState(startDate, newEndDate, timezone);

    updatePreviewDateTime({
      startDateTime: startDate,
      endDateTime: newEndDate,
      recurringPattern: recurringPattern,
      timeZone: timezone,
    });
  };

  const restrictEndDateToStart = (): void => {
    updateSelectedEndDate(selectedStartDateTime);
  };

  const onTimeZoneSelected = (selectedTimeZone: TimeZone): void => {
    updateErrorState(selectedStartDateTime, selectedEndDateTime, selectedTimeZone);
  };

  const updateShowCountdown = (): void => {
    const updatedShowCountdown = !isShowCountdown;
    dispatch(onEventWizardToggleCountdown(updatedShowCountdown));
    updatePreviewCountdown(updatedShowCountdown ? selectedStartDateTime : undefined, timezone);
  };

  const getDesktopDownwardArrowContent = (): React.ReactElement<any> => {
    return (
      <div className={`${styles.desktopArrow} flex-row-align-center _position-absolute`}>
        <svg xmlns="http://www.w3.org/2000/svg" width="8" height="21" viewBox="0 0 8 21" fill="none">
          <path
            d="M4.5 0.987305C4.5 0.711162 4.27614 0.487305 4 0.487305C3.72386 0.487305 3.5 0.711162 3.5 0.987305L4.5 0.987305ZM3.64645 20.1321C3.84171 20.3274 4.15829 20.3274 4.35355 20.1321L7.53553 16.9501C7.7308 16.7549 7.7308 16.4383 7.53553 16.243C7.34027 16.0478 7.02369 16.0478 6.82843 16.243L4 19.0715L1.17157 16.243C0.97631 16.0478 0.659728 16.0478 0.464465 16.243C0.269203 16.4383 0.269203 16.7549 0.464465 16.9501L3.64645 20.1321ZM3.5 0.987305L3.5 19.7786L4.5 19.7786L4.5 0.987305L3.5 0.987305Z"
            fill="#CBCBD0"
          />
        </svg>
      </div>
    );
  };

  const getMobileDownwardArrowContent = (): ReactElement<any> => {
    return (
      <div className="flex-row-align-center justify-content-center spacing-m-b-3 _full-width">
        <svg width="8" height="21" viewBox="0 0 8 21" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M4.5 1.20874C4.5 0.932598 4.27614 0.70874 4 0.70874C3.72386 0.70874 3.5 0.932598 3.5 1.20874L4.5 1.20874ZM3.64645 20.3536C3.84171 20.5488 4.15829 20.5488 4.35355 20.3536L7.53553 17.1716C7.7308 16.9763 7.7308 16.6597 7.53553 16.4645C7.34027 16.2692 7.02369 16.2692 6.82843 16.4645L4 19.2929L1.17157 16.4645C0.97631 16.2692 0.659728 16.2692 0.464465 16.4645C0.269203 16.6597 0.269203 16.9763 0.464465 17.1716L3.64645 20.3536ZM3.5 1.20874L3.5 20L4.5 20L4.5 1.20874L3.5 1.20874Z"
            fill="#CBCBD0"
          />
        </svg>
      </div>
    );
  };

  const onClickMobilePanelTimeZone = (): void => {
    onClickMobilePanelItem(DateTimeMobilePanel.TIMEZONE_SELECTOR);
  };

  const onClickMobilePanelStartTimeInput = (): void => {
    onClickMobilePanelItem(DateTimeMobilePanel.START_TIME_INPUT);
  };

  const onClickMobilePanelEndTimeInput = (): void => {
    onClickMobilePanelItem(DateTimeMobilePanel.END_TIME_INPUT);
  };

  const onClickMobilePanelItem = (panel: DateTimeMobilePanel): void => {
    dispatch(onEventWizardDateTimeMobilePanelUpdate(panel));
  };

  const getContent = (): ReactElement<any> => {
    if (!isMobile) {
      return getDefaultContent();
    }

    if (dateTimeMobilePanel === DateTimeMobilePanel.TIMEZONE_SELECTOR) {
      return getTimezoneMobilePanel();
    }

    if (dateTimeMobilePanel === DateTimeMobilePanel.START_DATE_CALENDAR) {
      return getStartDateCalendarPanel();
    }

    if (dateTimeMobilePanel === DateTimeMobilePanel.END_DATE_CALENDAR) {
      return getEndDateCalendarPanel();
    }

    if (dateTimeMobilePanel === DateTimeMobilePanel.START_TIME_INPUT) {
      return getStartTimeInputPanel();
    }

    if (dateTimeMobilePanel === DateTimeMobilePanel.END_TIME_INPUT) {
      return getEndTimeInputPanel();
    }

    if (dateTimeMobilePanel === DateTimeMobilePanel.RECURRING_TYPE_SELECTOR) {
      return getRecurringTypeMobilePanel();
    }

    if (dateTimeMobilePanel === DateTimeMobilePanel.RECURRING_ENDS_ON_SELECTOR) {
      return getRecurringEndsOnMobilePanel();
    }
    return getDefaultContent();
  };

  const getDefaultContent = (): ReactElement<any> => {
    return (
      <div className={isMobile ? 'spacing-p-t-3 spacing-p-r-6 spacing-p-l-6' : ''}>
        <div className="flex-row-align-center spacing-m-b-3 _position-relative">
          <Text className={`${styles.timeRowText} spacing-m-r-2 _full-width`} val={window.i18next.t('pmwjs_start')} size={TextSize.XXSMALL} bold />
          {!isMobile ? getDesktopDownwardArrowContent() : undefined}
          <DateInput
            minDate={new Date()}
            selectedDate={selectedStartDateTime}
            onSelectDate={updateSelectedStartDate}
            className="spacing-m-r-2 flex-row-align-center _full-width"
            mobilePanelType={isMobile ? DateTimeMobilePanel.START_DATE_CALENDAR : undefined}
          />
          <TimeInput
            defaultSelectedDateTime={selectedStartDateTime}
            onSelectTime={onStartTimeUpdateFromAmPmToggleOrDropdown}
            onTimeInputBlurCallback={updateSelectedStartTime}
            dropdownID={startTimeInputDropdownID}
            onClickSelector={isMobile ? onClickMobilePanelStartTimeInput : undefined}
          />
        </div>
        {isMobile ? getMobileDownwardArrowContent() : undefined}
        <div className="flex-row-align-center spacing-m-b-2 _position-relative">
          <Text className={`${styles.timeRowText} spacing-m-r-2 _full-width`} val={window.i18next.t('pmwjs_end')} size={TextSize.XXSMALL} bold />
          <DateInput
            isInErrorState={isDateErr}
            minDate={selectedStartDateTime}
            maxDate={getMaxAllowedDate(selectedStartDateTime)}
            selectedDate={selectedEndDateTime}
            onSelectDate={updateSelectedEndDate}
            className="spacing-m-r-2 flex-row-align-center _full-width"
            mobilePanelType={isMobile ? DateTimeMobilePanel.END_DATE_CALENDAR : undefined}
            isDisabled={recurringPattern.isRecurring}
          />
          <TimeInput
            isInErrorState={isTimeErr}
            defaultSelectedDateTime={selectedEndDateTime}
            onSelectTime={onEndTimeUpdateFromAmPmToggleOrDropdown}
            onTimeInputBlurCallback={updateSelectedEndTime}
            dropdownID={endTimeInputDropdownID}
            onClickSelector={isMobile ? onClickMobilePanelEndTimeInput : undefined}
          />
        </div>
        {errMsg ? <EventWizardErrorInfo text={errMsg} /> : undefined}
        <div className="flex-row-align-center justify-content-between spacing-m-b-2 _full-width">
          <Timezone onSelectTimeZone={onTimeZoneSelected} mobileDropdownClick={isMobile ? onClickMobilePanelTimeZone : undefined} />
        </div>
        <div className="flex-column-align-start spacing-m-b-3">
          <RecurringEventSelector endDate={selectedEndDateTime} isMobile={isMobile} onToggleRecurring={restrictEndDateToStart} />
        </div>
        <div className="flex-row-align-center">
          <Icon className="spacing-m-r-1" icon="icon-timer" size={IconSize.SIZE_ICON_16} />
          <div className="flex-row-align-center spacing-m-r-3">
            <Text val={window.i18next.t('pmwjs_countdown')} size={TextSize.XXSMALL} bold />
          </div>
          <ToggleSwitch isChecked={isShowCountdown} onChange={updateShowCountdown} size={ToggleSwitchV2Size.SMALL} />
        </div>
      </div>
    );
  };

  const getTimezoneMobilePanel = (): ReactElement<any> => {
    return (
      <div className="spacing-p-t-3 spacing-p-r-6 spacing-p-b-0 spacing-p-l-6">
        <TimezoneContent />
      </div>
    );
  };

  const getStartDateCalendarPanel = (): ReactElement<any> => {
    return (
      <div className="spacing-p-t-3 spacing-p-r-5 spacing-p-b-0 spacing-p-l-5">
        <DateInputCalendarContent minDate={new Date()} selectedDate={selectedStartDateTime} onDateSelected={updateSelectedStartDate} isMobile />
      </div>
    );
  };

  const getEndDateCalendarPanel = (): ReactElement<any> => {
    return (
      <div className="spacing-p-t-3 spacing-p-r-5 spacing-p-b-0 spacing-p-l-5">
        <DateInputCalendarContent
          minDate={selectedStartDateTime}
          maxDate={getMaxAllowedDate(selectedStartDateTime)}
          selectedDate={selectedEndDateTime}
          onDateSelected={updateSelectedEndDate}
          isMobile
        />
      </div>
    );
  };

  const getStartTimeInputPanel = (): ReactElement<any> => {
    return (
      <CalendarTimePicker
        hours={convert24To12Hr(selectedStartDateTime.getHours())}
        isPMChecked={is24hrPM(selectedStartDateTime.getHours())}
        minutes={selectedStartDateTime.getMinutes()}
        showScheduleButton={false}
        showBoxShadow={false}
        shouldRenderWarning={false}
        showOnlyTime
        isFullWidth
        onChangeTime={updateSelectedStartTimeFromTimePicker}
      />
    );
  };

  const getEndTimeInputPanel = (): ReactElement<any> => {
    return (
      <CalendarTimePicker
        hours={convert24To12Hr(selectedEndDateTime.getHours())}
        isPMChecked={is24hrPM(selectedEndDateTime.getHours())}
        minutes={selectedEndDateTime.getMinutes()}
        showScheduleButton={false}
        showBoxShadow={false}
        shouldRenderWarning={false}
        showOnlyTime
        isFullWidth
        onChangeTime={updateSelectedEndTimeFromTimePicker}
      />
    );
  };

  const getRecurringTypeMobilePanel = (): ReactElement<any> => {
    return (
      <div className="spacing-p-t-3 spacing-p-r-6 spacing-p-b-0 spacing-p-l-6">
        <EventWizardRecurringTypeContent />
      </div>
    );
  };

  const getRecurringEndsOnMobilePanel = (): ReactElement<any> => {
    return (
      <div className="spacing-p-t-3 spacing-p-r-5 spacing-p-b-0 spacing-p-l-5">
        <DateInputCalendarContent
          minDate={selectedEndDateTime}
          maxDate={getMaxRecurringDate(selectedStartDateTime, recurringType)}
          selectedDate={lastRecurringDate}
          onDateSelected={updateLastRecurringDate}
          isMobile
        />
      </div>
    );
  };

  return <div className={dateTimeMobilePanel !== DateTimeMobilePanel.DEFAULT ? `${styles.mobilePanel} slideIn -from-right` : ''}>{getContent()}</div>;
}
